/**
 * The one data-fetching hook this slice needs.
 *
 * It exists so every screen gets the same three states — loading, ready, error
 * — with the same rules: an in-flight request is aborted when its inputs change
 * or the screen unmounts, an abort is never shown as an error, and data already
 * on screen survives a refetch so the list does not blink on every keystroke.
 */
import { useEffect, useRef, useState, type DependencyList } from 'react';

import { isAbortError, toApiError, type ApiError } from './api-error';

export type AsyncStatus = 'loading' | 'ready' | 'error';

interface AsyncState<T> {
  readonly status: AsyncStatus;
  readonly data: T | null;
  readonly error: ApiError | null;
}

export interface AsyncData<T> extends AsyncState<T> {
  /** True while a reload runs over data that is still on screen. */
  readonly refreshing: boolean;
  readonly reload: () => void;
}

export function useAsyncData<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList,
): AsyncData<T> {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'loading',
    data: null,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  // `load` is a fresh closure every render; keeping the latest one in a ref lets
  // callers write it inline while `deps` alone decides when to re-fetch.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();

    setState((previous) => ({ status: 'loading', data: previous.data, error: null }));

    loadRef.current(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        setState({ status: 'ready', data, error: null });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState((previous) => ({
          status: 'error',
          data: previous.data,
          error: toApiError(error),
        }));
      },
    );

    return () => {
      controller.abort();
    };
  }, [...deps, attempt]);

  return {
    ...state,
    refreshing: state.status === 'loading' && state.data !== null,
    reload: () => {
      setAttempt((value) => value + 1);
    },
  };
}
